class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.form = document.getElementById(this.dataset.formId);
    this.productSection = document.getElementById(`ProductSection-${this.sectionId}`);
    this.realButton = this.form ? this.form.querySelector('[name="add"]') : null;

    this.button = this.querySelector('.js-sticky-atc-button');
    this.buttonText = this.querySelector('.js-sticky-atc-button-text');
    this.priceEl = this.querySelector('.js-sticky-atc-price');
    this.variantEl = this.querySelector('.js-sticky-atc-variant');
    this.imageEl = this.querySelector('.js-sticky-atc-image');

    if (!this.form || !this.realButton || !this.button) {
      this.remove();
      return;
    }

    this._onButtonClick = this.handleClick.bind(this);
    this.button.addEventListener('click', this._onButtonClick);

    this.picker = this.productSection
      ? this.productSection.querySelector('variant-radios, variant-selects, variant-mixed-inputs')
      : null;

    if (this.picker) {
      this._onVariantChange = this.handleVariantChange.bind(this);
      this.picker.addEventListener('variants:change', this._onVariantChange);
    }

    this.setupObserver();
    this.setupAddStateObserver();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.addStateObserver) this.addStateObserver.disconnect();
    if (this.button && this._onButtonClick) {
      this.button.removeEventListener('click', this._onButtonClick);
    }
    if (this.picker && this._onVariantChange) {
      this.picker.removeEventListener('variants:change', this._onVariantChange);
    }
    document.body.classList.remove('has-sticky-atc');
  }

  // Mirrors the real add-to-cart button's is-adding/is-added state onto the
  // sticky button, reusing the theme's existing spinner + checkmark CSS.
  // product-form.js only toggles those classes on the real button, and the
  // real add() call is 2-4 sequential network round trips (add.js, cart.js,
  // gift-wrapping update, recommendations refresh), so without this the
  // sticky button shows no feedback for up to ~1s.
  setupAddStateObserver() {
    const syncedClasses = ['is-adding', 'is-added'];
    const sync = () => {
      syncedClasses.forEach((cls) => {
        this.button.classList.toggle(cls, this.realButton.classList.contains(cls));
      });
    };

    this.addStateObserver = new MutationObserver(sync);
    this.addStateObserver.observe(this.realButton, {
      attributes: true,
      attributeFilter: ['class'],
    });
    sync();
  }

  setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        this.toggle(!entries[0].isIntersecting);
      },
      { threshold: 0 }
    );
    this.observer.observe(this.realButton);
  }

  toggle(show) {
    const isVisible = this.classList.contains('is-visible');
    if (show === isVisible) return;

    this.classList.toggle('is-visible', show);
    document.body.classList.toggle('has-sticky-atc', show);

    if (show) {
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--sticky-atc-offset', `${this.offsetHeight}px`);
      });
    }
  }

  handleVariantChange(event) {
    const variant = event.detail.variant;

    if (!variant) {
      this.button.setAttribute('disabled', 'disabled');
      if (this.buttonText) {
        this.buttonText.textContent = this.dataset.selectOptionText || 'Select an option';
      }
      return;
    }

    if (variant.available) {
      this.button.removeAttribute('disabled');
      if (this.buttonText) this.buttonText.textContent = this.dataset.addText || 'Add to cart';
    } else {
      this.button.setAttribute('disabled', 'disabled');
      if (this.buttonText) {
        this.buttonText.textContent = (window.theme && window.theme.t && window.theme.t.sold_out) || 'Sold out';
      }
    }

    if (this.priceEl && window.Shopify && window.Shopify.formatMoney) {
      this.priceEl.textContent = window.Shopify.formatMoney(
        variant.price,
        window.theme.money_product_price_format
      );
    }

    if (this.variantEl && variant.title) {
      this.variantEl.textContent = variant.title.replace(/ \/ /g, ' · ');
    }

    if (this.imageEl && variant.featured_image && variant.featured_image.src) {
      const src = this.resizeImage(variant.featured_image.src, 160);
      if (this.imageEl.tagName === 'IMG') {
        this.imageEl.src = src;
      }
    }
  }

  resizeImage(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  handleClick() {
    if (this.button.hasAttribute('disabled')) return;
    if (this.button.classList.contains('is-adding')) return;
    this.form.requestSubmit();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
